/**
 * Phase 11 — Call history panel for a single BDC lead.
 *
 * Fetches `GET /api/bdc/leads/{id}/calls` and renders inbound/outbound calls
 * touching the lead's phone number. Each row surfaces direction, duration,
 * sentiment, one-line AI summary, and expandable transcript + recording.
 */
import React, { useEffect, useState } from "react";
import { PhoneIncoming, PhoneOutgoing, Play, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const SENTIMENT_EMOJI = {
  positive: "😊",
  neutral: "😐",
  negative: "😟",
  frustrated: "😠",
};

const durationLabel = (secs) => {
  if (!secs || secs <= 0) return "—";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const BdcLeadCallHistory = ({ leadId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/bdc/leads/${leadId}/calls`)
      .then((r) => { if (!cancelled) setItems(r.data.items || []); })
      .catch((e) => { if (!cancelled) setError(formatApiError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [leadId]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="bdc-lead-calls-panel">
      <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Call history</p>
      {loading && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 size={14} className="animate-spin" /> Loading calls…
        </div>
      )}
      {!loading && error && (
        <p className="text-sm text-rose-600" data-testid="bdc-lead-calls-error">{error}</p>
      )}
      {!loading && !error && items.length === 0 && (
        <p className="text-sm text-slate-500">No calls with this contact yet.</p>
      )}
      {!loading && !error && items.length > 0 && (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {items.map((c) => {
            const isOut = c.direction === "outbound";
            const isExpanded = expanded === c.id;
            const Icon = isOut ? PhoneOutgoing : PhoneIncoming;
            const emoji = SENTIMENT_EMOJI[c.ai_sentiment] || "";
            return (
              <div key={c.id} className="border-b border-slate-100 last:border-0 pb-2 last:pb-0" data-testid={`bdc-lead-call-${c.id}`}>
                <div className="flex items-start gap-2">
                  <Icon size={14} className={`mt-0.5 shrink-0 ${isOut ? "text-blue-500" : "text-emerald-500"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="text-slate-500 font-mono">
                        {c.started_at ? new Date(c.started_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                      </span>
                      <span className="text-slate-400 whitespace-nowrap">
                        {durationLabel(c.duration_seconds)}
                        {emoji && <span className="ml-1">{emoji}</span>}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-slate-700 line-clamp-2">
                      {c.ai_summary || (c.transcript ? c.transcript.slice(0, 140) : "No AI summary yet.")}
                    </p>
                    <div className="mt-1 flex items-center gap-2">
                      {c.recording_url && (
                        <a
                          href={c.recording_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] text-brand hover:underline"
                          data-testid={`bdc-lead-call-play-${c.id}`}
                        >
                          <Play size={11} /> Play recording
                        </a>
                      )}
                      {c.transcript && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-5 px-1 text-[11px] text-slate-500 hover:text-slate-900"
                          onClick={() => setExpanded(isExpanded ? null : c.id)}
                          data-testid={`bdc-lead-call-toggle-${c.id}`}
                        >
                          {isExpanded ? <><ChevronUp size={11} /> Hide transcript</> : <><ChevronDown size={11} /> Full transcript</>}
                        </Button>
                      )}
                    </div>
                    {isExpanded && c.transcript && (
                      <div className="mt-2 rounded-md bg-slate-50 border border-slate-100 p-2 text-[11px] text-slate-600 whitespace-pre-line" data-testid={`bdc-lead-call-transcript-${c.id}`}>
                        {c.transcript}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BdcLeadCallHistory;
